import { create } from 'zustand'
import { subscribeWithSelector } from 'zustand/middleware'
import {
  CLUSTER_ENDPOINT,
  CONNECTION_COMMITMENT,
  LITE_RPC_URL,
  makeRpcUrlsFromStorageWithFallback,
  RPC_PROVIDER_URL_KEY_WITHOUT_DEFAULT,
  SOLANA_TOKEN_DETAILS,
  TRITON_URL,
  USDC_MINT,
  USDC_TOKEN_DETAILS,
} from 'utils/constants'
import { BN, web3 } from '@coral-xyz/anchor'
import { Connection, PublicKey } from '@solana/web3.js'
import { produce } from 'immer'
import { fetchMetadataForMints, TokenAccount } from 'utils/tokens'
import { TransactionNotificationType } from 'utils/notifications'
import { INITIAL_SIDE_STATS, SideStats, Trade } from 'hooks/useRecentTrades'
import { Market } from '@cks-systems/manifest-sdk'
import { IOrderLineAdapter } from '@public/charting_library/charting_library'
import { readFromLocalStorage } from 'hooks/useLocalStorageState'
import { TokenMetadataItem } from 'types/token'
import Decimal from 'decimal.js'
import { floorToDecimal, formatNumericValue } from 'utils/numbers'
import { determineQuoteToken } from 'utils/trade'

const metadataCache = new Map<string, TokenMetadataItem[]>()

export interface Order {
  baseMint: string
  quoteMint: string
  orderId: BN
  openOrdersAddress: PublicKey
  openOrdersSlot: number
  price: number
  priceLots: BN
  size: number
  feeTier: number
  sizeLots: BN
  side: 'buy' | 'sell'
  clientId?: BN
}

export interface ManifestMarket {
  market: Market
  tickDecimals: number
  minOrderSizeDecimals: number
}

export const INITIAL_SWAP_TOKENS = {
  inputToken: USDC_TOKEN_DETAILS,
  outputToken: SOLANA_TOKEN_DETAILS,
}

type Store = {
  actions: {
    updateConnection: (url: string) => void
    setNewOutputToken: (
      newOutputMint: string,
      newOutputToken?: TokenMetadataItem,
    ) => Promise<void>
    setNewInputToken: (
      newInputMint: string,
      newInputToken?: TokenMetadataItem,
    ) => Promise<void>
    setMarketAndSwapToken: (
      baseMint: string,
      quoteMint?: string,
      baseTokenDetails?: TokenMetadataItem,
      quoteTokenDetails?: TokenMetadataItem,
    ) => void
    setCalculatedLimitFormOutputAmount: (
      inputAmount: string,
      price: string,
      tickDecimals: number,
    ) => void
    setCalculatedLimitFormValuesFromOutputAmount: (outputAmount: string) => void
  }
  connection: Connection
  endpointUrl: string
  market: {
    baseToken: TokenMetadataItem
    quoteToken: TokenMetadataItem
    manifestMarket: ManifestMarket | undefined
    loadingManifestMarket: boolean
  }
  newBuildAvailable: boolean
  openOrders: Order[]
  routeLoading: boolean
  readonly set: (x: (x: Store) => void) => void
  swap: {
    amountIn: string
    inputToken: TokenMetadataItem
    outputToken: TokenMetadataItem
    slippage: number
    swapMode: 'ExactIn' | 'ExactOut'
  }
  tokenList: {
    filterParams: string
  }
  trade: {
    limitForm: {
      price: string
      inputAmount: string
      outputAmount: string
    }
  }
  trades: {
    realtimeTrades: Trade[]
    buyStats: SideStats
    sellStats: SideStats
  }
  tradingView: {
    orderLines: Map<string | BN, IOrderLineAdapter>
  }
  transactionNotificationIdCounter: number
  transactionNotifications: Array<TransactionNotificationType>
  wallet: {
    tokens: TokenAccount[]
  }
  window: {
    width: number
    height: number
  }
}

const createBackupConnections = (
  primaryConnection: Connection,
): Connection[] => {
  const liteRpcConnection = new Connection(LITE_RPC_URL)
  const backupConnections = [liteRpcConnection]
  if (primaryConnection.rpcEndpoint !== TRITON_URL) {
    const conn = new Connection(TRITON_URL)
    backupConnections.push(conn)
  }
  return backupConnections
}

const store = create<Store>()(
  subscribeWithSelector((rawStoreSet, get) => {
    const set: Store['set'] = (fn) => rawStoreSet(produce(fn))

    const { url, wsUrl } = makeRpcUrlsFromStorageWithFallback(
      readFromLocalStorage(RPC_PROVIDER_URL_KEY_WITHOUT_DEFAULT),
    )

    let endpointUrl: string
    let connection: Connection
    try {
      endpointUrl = url
      connection = new web3.Connection(url, {
        wsEndpoint: wsUrl,
        commitment: CONNECTION_COMMITMENT,
      })
    } catch (e) {
      console.error(
        `Failed to create connection from url ${url}, falling back to ${CLUSTER_ENDPOINT.url}`,
        e,
      )
      endpointUrl = CLUSTER_ENDPOINT.url
      connection = new web3.Connection(CLUSTER_ENDPOINT.url, {
        wsEndpoint: CLUSTER_ENDPOINT.wsUrl,
        commitment: CONNECTION_COMMITMENT,
      })
    }

    return {
      connection,
      endpointUrl,
      market: {
        baseToken: SOLANA_TOKEN_DETAILS,
        quoteToken: USDC_TOKEN_DETAILS,
        manifestMarket: undefined,
        loadingManifestMarket: false,
      },
      newBuildAvailable: false,
      openOrders: [],
      routeLoading: false,
      set,
      swap: {
        amountIn: '',
        inputToken: INITIAL_SWAP_TOKENS.inputToken,
        outputToken: INITIAL_SWAP_TOKENS.outputToken,
        slippage: 0.5,
        swapMode: 'ExactIn',
      },
      tokenList: {
        filterParams: '',
      },
      trade: {
        limitForm: {
          price: '',
          inputAmount: '',
          outputAmount: '',
        },
      },
      trades: {
        realtimeTrades: [],
        buyStats: INITIAL_SIDE_STATS,
        sellStats: INITIAL_SIDE_STATS,
      },
      tradingView: {
        orderLines: new Map(),
      },
      transactionNotificationIdCounter: 0,
      transactionNotifications: [],
      wallet: {
        tokens: [],
      },
      window: {
        width: 0,
        height: 0,
      },
      actions: {
        updateConnection(endpointUrl) {
          const newConnection = new web3.Connection(
            endpointUrl,
            CONNECTION_COMMITMENT,
          )
          set((state) => {
            state.endpointUrl = endpointUrl
            state.connection = newConnection
          })
        },
        async setNewOutputToken(
          newOutputMint: string,
          newOutputToken?: TokenMetadataItem,
        ) {
          if (!newOutputToken) {
            const connection = get().connection
            newOutputToken = (
              await fetchMetadataForMints(
                [newOutputMint],
                undefined,
                connection,
              )
            )?.[0]
          }

          if (!newOutputToken) {
            console.error(
              `Tried to set new output token but failed to fetch token metadata: mint=${newOutputMint}`,
            )
            return
          }
          // Use priority logic for quote/base
          const previousInputToken = get().swap.inputToken
          const { quoteToken, baseToken } = determineQuoteToken(
            newOutputToken,
            previousInputToken,
          )
          set((state) => {
            state.swap.outputToken = newOutputToken
            state.swap.inputToken = previousInputToken
            state.market.baseToken = baseToken
            state.market.quoteToken = quoteToken
          })
        },
        async setNewInputToken(
          newInputMint: string,
          newInputToken?: TokenMetadataItem,
        ) {
          if (!newInputToken) {
            const connection = get().connection
            newInputToken = (
              await fetchMetadataForMints([newInputMint], undefined, connection)
            )?.[0]
          }

          if (!newInputToken) {
            console.error(
              `Tried to set new input token but failed to fetch token metadata: mint=${newInputMint}`,
            )
            return
          }
          // Use priority logic for quote/base
          const previousOutputToken = get().swap.outputToken
          const { quoteToken, baseToken } = determineQuoteToken(
            previousOutputToken,
            newInputToken,
          )
          set((state) => {
            state.swap.inputToken = newInputToken
            state.swap.outputToken = previousOutputToken
            state.market.baseToken = baseToken
            state.market.quoteToken = quoteToken
          })
        },
        async setMarketAndSwapToken(
          baseMint: string,
          quoteMint?: string,
          baseToken?: TokenMetadataItem,
          quoteToken?: TokenMetadataItem,
        ) {
          const fetchBaseToken = !baseToken
          const fetchQuoteToken = !quoteToken && quoteMint

          if (fetchBaseToken || fetchQuoteToken) {
            const connection = get().connection
            const tokensMetadata = await fetchMetadataForMints(
              [
                ...(fetchBaseToken ? [baseMint] : []),
                ...(fetchQuoteToken ? [quoteMint] : []),
              ],
              metadataCache,
              connection,
            )

            if (fetchBaseToken) {
              baseToken = tokensMetadata.find(({ mint }) => mint === baseMint)
            }
            if (fetchQuoteToken) {
              quoteToken = tokensMetadata.find(({ mint }) => mint === quoteMint)
            }
          }

          if (!baseToken) {
            baseToken =
              baseMint === USDC_MINT ? USDC_TOKEN_DETAILS : SOLANA_TOKEN_DETAILS
          }
          if (!quoteToken) {
            quoteToken =
              baseMint === USDC_MINT ? SOLANA_TOKEN_DETAILS : USDC_TOKEN_DETAILS
          }

          // Use priority logic for quote/base
          const { quoteToken: q, baseToken: b } = determineQuoteToken(
            baseToken,
            quoteToken,
          )
          set((state) => {
            state.market.baseToken = b
            state.swap.inputToken = b
            state.market.quoteToken = q
            state.swap.outputToken = q
          })
        },
        setCalculatedLimitFormOutputAmount(
          newInputAmount: string,
          newPrice: string,
          tickDecimals: number,
        ) {
          const inputAmount = new Decimal(Number(newInputAmount || '0'))

          const inputToken = get().swap.inputToken
          const outputToken = get().swap.outputToken
          const quoteToken = get().market.quoteToken

          const isBid = inputToken.mint === quoteToken.mint
          const price = new Decimal(Number(newPrice || '0'))

          // Use the output token's decimals for rounding the output amount,
          // not tickDecimals (which is for price precision). Using tickDecimals
          // causes severe truncation for high-price assets like BTC where
          // tickDecimals can be as low as 3, turning 0.001467 into 0.001.
          const outputDecimals = outputToken?.decimals ?? 6

          let formattedOutputAmount = ''
          if (
            !inputAmount.isNaN() &&
            inputAmount.gt(0) &&
            !price.isNaN() &&
            price.gt(0)
          ) {
            const outputAmount = isBid
              ? inputAmount.div(price)
              : inputAmount.mul(price)
            formattedOutputAmount = formatNumericValue(
              outputAmount.toDecimalPlaces(outputDecimals, Decimal.ROUND_FLOOR),
              outputDecimals,
            )
          }
          set((s) => {
            s.trade.limitForm.outputAmount = formattedOutputAmount
          })
        },
        setCalculatedLimitFormValuesFromOutputAmount(newOutputAmount: string) {
          const outputAmount = new Decimal(Number(newOutputAmount || '0'))
          if (outputAmount.isNaN() || outputAmount.lte(0)) {
            return
          }

          const price = new Decimal(Number(get().trade.limitForm.price || '0'))
          const inputAmount = new Decimal(
            Number(get().trade.limitForm.inputAmount || '0'),
          )
          const inputToken = get().swap.inputToken
          const quoteToken = get().market.quoteToken

          const isBid = inputToken.mint === quoteToken.mint
          if (!price.isNaN() && price.gt(0)) {
            const inputAmount = isBid
              ? outputAmount.mul(price)
              : outputAmount.div(price)
            set((s) => {
              s.trade.limitForm.inputAmount = floorToDecimal(
                inputAmount,
                inputToken.decimals,
              ).toFixed()
            })
          } else if (!inputAmount.isNaN() && inputAmount.gt(0)) {
            const price = isBid
              ? inputAmount.div(outputAmount)
              : outputAmount.div(inputAmount)
            set((s) => {
              s.trade.limitForm.price = floorToDecimal(
                price,
                quoteToken.decimals,
              ).toFixed()
            })
          }
        },
      },
    }
  }),
)

export default store
